import { AnchorButton, Classes, Colors, Intent, PopoverPosition, Tooltip } from '@blueprintjs/core'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { ElementJoiner } from '../../../../../../components/ElementJoiner'
import { ListingManagerLink } from '../../../../../../components/links/ListingManagerLink'
import NiceListingStatus from '../../../../../../components/NiceListingStatus'
import { TextSeparatorDot } from '../../../../../../components/TextSeparatorDot'
import Channels, { channelById, deprecatedChannelIds } from '../../../../../../constants/Channels'
import { usePermissions } from '../../../../../../hooks/usePermissions'
import { UprightStyle } from '../../../../../../types'
import { Listing } from '../../types'
import {
  formatDateTime,
  formatMoneyWithListingCurrency,
  formatMoneyWithListingCurrencyRange,
  resolveBinPrice
} from '../../utils'
import { ListingProperty } from '../ListingProperty'
import { toPercentage } from '../../../../../../utils'

interface Props {
  listing: Listing
  hideViews?: boolean
  hideClickThroughRateAndImpressions?: boolean
  variantsPriceRange?: number[]
  hasPriceOverride?: boolean
}

const styles: UprightStyle = {
  propertyRow: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 0 auto',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '4px 0'
  },
  channelName: {
    marginBottom: 4
  },
  dateRange: {
    fontStyle: 'italic',
    color: Colors.GRAY3,
    paddingBottom: '4px 0'
  }
}

interface AppState {
  current: any
}

export const ListingInfo: React.FC<Props> = props => {
  const { permissions } = usePermissions()
  const country = useSelector((state: AppState) => state.current.organization.address.country)
  const channel = channelById(props.listing.marketId)
  const isAuction =
    props.listing.postType === 'auction' ||
    (props.listing.marketId !== Channels.SHOPIFY.id && props.listing.concurrentType !== 'CONCURRENT')
  const binPrice = resolveBinPrice(props.listing.binPrice, channel.id)
  const endTime = props.listing.endTime || props.listing.projectedEndTime

  const isShopify = props.listing.marketId === Channels.SHOPIFY.id
  const channelDeprecated = deprecatedChannelIds.includes(props.listing.marketId)
  const isEbay = props.listing.marketId === Channels.EBAY.id
  const isAmazon = props.listing.marketId === Channels.AMAZON.id

  let itemLink
  if (isShopify) {
    itemLink = props.listing.listingUrl ? `//${props.listing.listingUrl}` : null
  } else if (isEbay) {
    const tld = country === 'CA' ? 'ca' : 'com'
    itemLink = channel.itemLink(props.listing.externalMarketId, tld)
  } else if (isAmazon) {
    itemLink = props.listing.asin ? channel.itemLink(props.listing.asin) : null
  } else {
    itemLink = channel.itemLink(props.listing.externalMarketId)
  }

  return (
    <>
      {props.listing.startTime && endTime && (
        <div style={styles.dateRange} className={Classes.TEXT_SMALL}>
          {formatDateTime(props.listing.startTime)}
          {props.listing.startTime && endTime && ' – '}
          {endTime && !props.listing.endTime && 'Est. '}
          {endTime && formatDateTime(endTime)}
        </div>
      )}
      <div style={styles.propertyRow}>
        <ElementJoiner separator={<TextSeparatorDot color={Colors.LIGHT_GRAY1} />}>
          <NiceListingStatus value={props.listing.state} errors={props.listing.externalErrors} />
          {(isAmazon ? props.listing.asin : props.listing.externalMarketId) && (
            <Tooltip
              content="Ended listings are not viewable on Shopify"
              disabled={itemLink || !isShopify}
              position={PopoverPosition.BOTTOM}
            >
              <AnchorButton
                disabled={!itemLink}
                className="bp3-button-as-link"
                intent="primary"
                minimal
                small
                target="_blank"
                rel="noreferrer"
                href={itemLink}
              >
                {(() => {
                  if (props.listing.marketId === Channels.SHOPIFY.id) return 'View on Shopify'
                  if (isAmazon) return 'View on Amazon'
                  return `#${props.listing.externalMarketId}`
                })()}
              </AnchorButton>
            </Tooltip>
          )}
          {props.listing.listingId && permissions.includes('listings.view') && !channelDeprecated && (
            <ListingManagerLink
              channelId={props.listing.marketId}
              viewListingId={props.listing.listingId}
              marketAccountId={props.listing.marketAccountId}
            >
              See listing details
            </ListingManagerLink>
          )}
          {channelDeprecated && props.listing.listingId && permissions.includes('listings.view') && (
            <Tooltip content="Channel no longer available">
              <AnchorButton
                disabled
                text="See listing details"
                className="bp3-button-as-link"
                minimal
                intent={Intent.PRIMARY}
              />
            </Tooltip>
          )}
        </ElementJoiner>
      </div>
      <div style={styles.propertyRow}>
        {props.variantsPriceRange?.length === 2 && (
          <ListingProperty
            name="Price"
            value={`${props.hasPriceOverride ? 'Override: ' : ''}${formatMoneyWithListingCurrencyRange(
              props.variantsPriceRange[0],
              props.variantsPriceRange[1],
              props.listing.currencyCode
            )}`}
          />
        )}
        {!(props.variantsPriceRange?.length === 2) && props.listing.startPrice && (
          <ListingProperty
            name={isAuction ? 'Starting Price' : 'Price'}
            value={`${props.hasPriceOverride ? 'Override: ' : ''}${formatMoneyWithListingCurrency(
              props.listing.startPrice,
              props.listing.currencyCode
            )}`}
          />
        )}
        {isAuction && props.listing.bidCount > 0 && (
          <ListingProperty
            name="Current price"
            value={
              props.listing.currentPrice &&
              formatMoneyWithListingCurrency(props.listing.currentPrice, props.listing.currencyCode)
            }
          />
        )}
        {props.listing.shippingPrice && (
          <ListingProperty
            name="Shipping price"
            value={
              props.listing.shippingPrice &&
              formatMoneyWithListingCurrency(props.listing.shippingPrice, props.listing.currencyCode)
            }
          />
        )}
        {isAuction && (
          <ListingProperty name="Bids" value={props.listing.bidCount === 0 ? null : props.listing.bidCount} />
        )}
        {binPrice && (
          <ListingProperty
            name="Buy now price"
            value={formatMoneyWithListingCurrency(binPrice, props.listing.currencyCode)}
          />
        )}
        {!props.hideViews && <ListingProperty name="Views" value={props.listing.viewCount} />}
        {!props.hideClickThroughRateAndImpressions && (
          <>
            <ListingProperty name="Click Through Rate" value={toPercentage(props.listing.clickThroughRate)} />
            <ListingProperty name="Impressions" value={props.listing.listingImpressionTotal} />
          </>
        )}
      </div>
    </>
  )
}
